'use client';

import { useCallback, useMemo, memo } from 'react';
import { useTranslations } from 'next-intl';
import { Position, PRODUCT_NOTIFY_ME, PRODUCT_STORE_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { SideMenuModal, useToasts, useModalActions } from '@headless-workspace/core-ui';
import {
    CtaType,
    NotifyMeModal,
    ProductDetailInfoCTA,
    ProductDetailInfoModal,
    ProductDetailInfoModalContext,
    ProductType,
    StoresDeliveryContainer,
    useClientData,
    useDeliveryReturnsModal,
    useProductDeliveryBusinessRules,
    useProductDetailContext,
    useUserInfo,
} from '@headless-workspace/domain/common/client';
import { GlowButton, GlowDivider, Icons, PropsWithLocale } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { DeliveryInformation } from './DeliveryInformation';

type ProductDetailDeliveryZoneContainerProps = PropsWithLocale;

export const ProductDetailDeliveryZoneContainer: ComponentMemo<ProductDetailDeliveryZoneContainerProps> = memo(
    ({ locale }) => {
        const discoverTranslations = useTranslations('Discover.Pdp.deliveryZone');
        const commonTranslations = useTranslations('Modal.stores');
        const { notifyError } = useToasts();

        const { result: userInfoResult, isLoading: isUserInfoLoading } = useUserInfo();
        const userInfo = unwrapResultSuccess(userInfoResult);

        const { productDetails } = useProductDetailContext();
        // The case where the sku is not defined is impossible at this level, as the component is only rendered for products that are not PRODUCT_SET.
        const sku = productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : undefined;

        const deliveryInformation = useProductDeliveryBusinessRules({ userInfo });

        const { getClientFlags } = useClientData();

        const isClickAndCollectDisplayed = getClientFlags('isClickAndCollectDisplayed');
        const isDeliveryReturnsInfoDisplayed = getClientFlags('isDeliveryReturnsInfoDisplayed');

        const { addIfAbsentAndOpen } = useModalActions();

        const { openModal: openDeliveryReturnsModal } = useDeliveryReturnsModal({
            notifyError,
            translationsErrorMessage: discoverTranslations('errors.informationFetchError'),
        });

        const openNotifyMeModal = useCallback(() => {
            if (!sku) {
                return;
            }

            addIfAbsentAndOpen(
                PRODUCT_NOTIFY_ME,
                SideMenuModal({
                    side: Position.Right,
                    map: {
                        default: <NotifyMeModal locale={locale} productIds={[sku]} />,
                    },
                }),
            );
        }, [addIfAbsentAndOpen, locale, sku]);

        const renderNotifyMeCTA = useCallback(
            (label: string) => (
                <GlowButton label={label} onClick={openNotifyMeModal} Icon={Icons.Notification} variant={'secondary'} />
            ),
            [openNotifyMeModal],
        );

        const selectStoreButtonElement = useCallback(
            (cta: { label: string; type: CtaType }, onClick: () => void) => (
                <GlowButton label={cta.label} onClick={onClick} variant={'secondary'} />
            ),
            [],
        );

        const warehouseCTA = useMemo(() => {
            if (deliveryInformation.isLoading) {
                return undefined;
            }

            if (deliveryInformation.warehouse.cta?.type === CtaType.NotifyMe) {
                return renderNotifyMeCTA(deliveryInformation.warehouse.cta.label);
            }

            return undefined;
        }, [deliveryInformation, renderNotifyMeCTA]);

        const storeCTA = useMemo(() => {
            if (deliveryInformation.isLoading) {
                return undefined;
            }

            const { cta } = deliveryInformation.store;

            switch (cta?.type) {
                case CtaType.SelectStore:
                case CtaType.ChangeStore: {
                    return (
                        <ProductDetailInfoCTA
                            modalId={PRODUCT_STORE_MENU_ID}
                            menu={
                                <ProductDetailInfoModal
                                    title={commonTranslations('label.storeAvailability')}
                                    renderInformation={<StoresDeliveryContainer locale={locale} ctaLabel={cta.label} />}
                                    context={ProductDetailInfoModalContext.storeSelection}
                                    isSidePanelDeliveryZone={true}
                                    removeModalOnClose
                                    productModalId={PRODUCT_STORE_MENU_ID}
                                />
                            }
                            buttonElement={(onClick) => selectStoreButtonElement(cta, onClick)}
                        />
                    );
                }
                case CtaType.NotifyMe:
                    return renderNotifyMeCTA(cta.label);
                default:
                    return undefined;
            }
        }, [deliveryInformation, locale, renderNotifyMeCTA, selectStoreButtonElement, commonTranslations]);

        if (deliveryInformation.isLoading || isUserInfoLoading) {
            return (
                <div
                    id={'product-delivery-zone'}
                    className={
                        'flex flex-col gap-1.5 border-medium border-solid border-border-primary rounded-0.5 py-1.5'
                    }
                >
                    <DeliveryInformation isLoading />
                    <GlowDivider />
                    <DeliveryInformation isLoading />
                </div>
            );
        }

        return (
            <div
                id={'product-delivery-zone'}
                className={'flex flex-col gap-1.5 border-medium border-solid border-border-primary rounded-0.5 py-1.5'}
            >
                <DeliveryInformation
                    isLoading={deliveryInformation.isLoading}
                    Icon={Icons.Truck}
                    title={deliveryInformation.warehouse?.title}
                    description={deliveryInformation.warehouse?.description}
                    status={deliveryInformation.warehouse?.status}
                    cta={warehouseCTA}
                />
                {isClickAndCollectDisplayed && (
                    <>
                        <GlowDivider />
                        <DeliveryInformation
                            isLoading={deliveryInformation.isLoading}
                            Icon={Icons.Storefront}
                            title={deliveryInformation.store.title}
                            description={deliveryInformation.store.description}
                            status={deliveryInformation.store.status}
                            cta={storeCTA}
                        />
                    </>
                )}
                {isDeliveryReturnsInfoDisplayed && (
                    <>
                        <GlowDivider />
                        <ProductDetailInfoCTA
                            labelElement={discoverTranslations('action.deliveryAndReturns')}
                            className={'px-1.5 text-text-primary'}
                            onClick={openDeliveryReturnsModal}
                        />
                    </>
                )}
            </div>
        );
    },
);

ProductDetailDeliveryZoneContainer.displayName = 'ProductDetailDeliveryZoneContainer';
